import { GestButton, GestShell, getBrowserLanguage, setSession, setSettings, setSnackbar, SettingsModel } from '@shared/library';
import { loadCitiesAndSelected } from '@webgest/library';
import { languages } from '@shared/library/dist/static/language';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import packageJson from '../../../package.json';
import config from '../../config';
import importer from '../../helpers/importer';
import { MenuArea_model } from '../../models/ui_data/MenuArea_model';
import './Site.scss';
//ICONS
import { CheckRounded, HelpOutlineRounded, PublicRounded } from '@mui/icons-material';
//CONTEXTS
import { CntxConfig } from '../WebGest';
//STATIC
import { AVAILABLE_LANGUAGES, HOME_PATH, SHELL_BRAND } from '../../static/constants';
import { useSessionPolling } from '../../static/hooks';
import { buildMenu, toShellUser } from '../../static/methods';
import { getRoutes } from './routes';

const Site = () => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //#region STATIC DATA
    const lang = languages(cntxConfig.settings?.lang);
    const navigate = useNavigate();
    const { pathname } = useLocation(); // without the basename of the app (the shell would otherwise read /PreProd/geotool and find no route)
    const userData = cntxConfig.session?.userData;
    const isAdmin = userData?.role === 'admin';
    const currentLang = cntxConfig.settings?.lang ?? getBrowserLanguage();
    //#endregion

    //#region STATES
    const [areas, setAreas] = React.useState<MenuArea_model[]>();
    //#endregion

    //the menu is only data (areas with their options, disabled ones dimmed): the shell gives each entry the page and the icon of the route with the same path
    const menu = React.useMemo(() => buildMenu(areas ?? [], lang, isAdmin), [areas, cntxConfig.settings?.lang, isAdmin]);

    //#region METHODS

    //#region API CALLS
    const getMenuAPI = () =>
        axios.get<{ data: MenuArea_model[]; }>(`${config.api.internal}/Settings/menu`, { withCredentials: true })
            .then(res => {
                localStorage.setItem('menu', JSON.stringify(res.data.data));
                setAreas(res.data.data);
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setAreas([]);
            });

    const getSettingsAPI = () =>
        axios.get<{ data: SettingsModel; }>(`${config.api.internal}/Settings`, { withCredentials: true })
            .then(res => {
                localStorage.setItem('settings', JSON.stringify(res.data.data));
                setSettings(cntxConfig, res.data.data);
            })
            .catch((err: AxiosError) => console.error(err));

    const logoutAPI = () =>
        axios.delete(`${config.api.internal}/Auth/logout`, { withCredentials: true })
            .then(() => setSession(cntxConfig))
            .catch(() => setSnackbar(cntxConfig, { message: "Errore durante chiusura della sessione corrente", severity: "error" }));

    const updateLanguageAPI = (langCode: string) =>
        axios.patch(`${config.api.internal}/Account/Language/ChangeTo/${langCode}`, undefined, { withCredentials: true })
            .then(() => {
                const settingsLS = JSON.parse(localStorage.getItem('settings') || '{}');

                settingsLS.lang = langCode;
                setSettings(cntxConfig, settingsLS);
                localStorage.setItem('settings', JSON.stringify(settingsLS));
            })
            .catch(() => setSnackbar(cntxConfig, { message: "Errore salvataggio della lingua scelta", severity: "error" }));
    //#endregion

    //#endregion

    useSessionPolling(cntxConfig);

    //STATES CHANGE
    React.useEffect(() => {
        loadCitiesAndSelected(config.api.internal, cntxConfig);
    }, [userData]);

    React.useEffect(() => {
        //menu and settings are cached in the browser: the api is called only when there is nothing stored
        const storedMenu = localStorage.getItem('menu');
        const storedSettings = localStorage.getItem('settings');

        if (storedMenu) {
            setAreas(JSON.parse(storedMenu));
        }
        else {
            getMenuAPI();
        }

        if (storedSettings) {
            setSettings(cntxConfig, JSON.parse(storedSettings));
        }
        else {
            getSettingsAPI();
        }
    }, []);

    return (
        <GestShell
            appName={SHELL_BRAND.appName}
            routing={{ navigate, pathname, homePath: HOME_PATH }}
            labels={{ logout: lang.logout }}
            sidebar={{ menu, routes: getRoutes(cntxConfig), loading: !areas || !!cntxConfig.cache?.cities.loading, variant: "flyout", logo: SHELL_BRAND.sidebarLogo }}
            header={{
                logo: SHELL_BRAND.logo,
                logoTooltip: "Version " + packageJson.version,
                actions: localStorage.getItem('support') === '1' &&
                    <GestButton iconBtn tooltip="Support" icon={<HelpOutlineRounded />} onclick={() => window.open(config.platforms.support, '_blank')} />,
                user: {
                    data: toShellUser(userData!),
                    onLogout: logoutAPI,
                    menu: [{
                        label: lang.language,
                        path: "language",
                        icon: <PublicRounded />,
                        children: AVAILABLE_LANGUAGES.map(l => ({
                            label: lang.universal.languages[l.name as keyof typeof lang.universal.languages],
                            path: `language-${l.id}`,
                            icon: <img src={importer.flag.require(`${l.iconName ?? l.id}.svg`)} alt="" loading="lazy" />,
                            end: currentLang === l.id ? <CheckRounded /> : undefined,
                            onClick: () => updateLanguageAPI(l.id),
                        }))
                    }],
                },
            }}
        >
        </GestShell>
    );
};
export default React.memo(Site);
